import { Injectable } from '@angular/core';

/**
 * interfaces
 */
import { IClipboardResult } from './interfaces';

/**
 * @service ClipboardService
 * @description Thin wrapper around the browser Clipboard API.
 * Provides three focused methods — one per content type — each mapping directly
 * to the native `navigator.clipboard` API. The browser manages permissions automatically.
 *
 * @example
 * // text
 * const result = await this._clipboard.writeText('Hello');
 *
 * @example
 * // html
 * const result = await this._clipboard.writeHtml('<b>Bold</b>');
 *
 * @example
 * // image
 * const result = await this._clipboard.writeImage(imgElement);
 */
@Injectable({ providedIn: 'root' })
export class ClipboardService {

  /**
   * @description Copies plain text to the clipboard using `navigator.clipboard.writeText()`.
   * @param value String or number to copy. Numbers are converted to string automatically.
   * @returns {Promise<IClipboardResult>}
   */
  public async writeText(value: string | number): Promise<IClipboardResult> {
    try {
      await navigator.clipboard.writeText(String(value));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  /**
   * @description Copies an HTML string to the clipboard using `navigator.clipboard.write()`
   * with MIME type `text/html`.
   * @param html The HTML string to copy.
   * @returns {Promise<IClipboardResult>}
   */
  public async writeHtml(html: string): Promise<IClipboardResult> {
    try {
      const blob = new Blob([html], { type: 'text/html' });
      await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  /**
   * @description Copies an image to the clipboard using `navigator.clipboard.write()`.
   * Accepts a `Blob`, `File`, or `HTMLImageElement`.
   * For `HTMLImageElement`, the image `src` is fetched automatically to obtain the `Blob`.
   * @param source The image source to copy.
   * @returns {Promise<IClipboardResult>}
   */
  public async writeImage(source: Blob | File | HTMLImageElement): Promise<IClipboardResult> {
    try {
      const blob = await this._toBlob(source);
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  //--------------------------------------------------------------------------
  // Private
  //--------------------------------------------------------------------------

  /**
   * @description Resolves any image source to a `Blob`.
   * `Blob` and `File` are returned as-is. `HTMLImageElement` is fetched via its `src`.
   * @param source Image source.
   */
  private async _toBlob(source: Blob | File | HTMLImageElement): Promise<Blob> {
    if (source instanceof Blob) return source;
    const response = await fetch(source.currentSrc || source.src);
    return response.blob();
  }

}
